import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CollectionsAPI, { Collection, CollectionVisibilityFilter, Item } from '../api/CollectionsAPI';
import { toastService } from '../services/ToastService';
import { buildItemPayloadForMoveToCollection } from '../shared/collectionCatalogMove';
import { simplifyUrl } from '../shared/urlUtils';
import { useImageUploader } from './useImageUploader';

export type CollectionListFilters = {
  visibility?: CollectionVisibilityFilter[];
  pricesArePublic?: boolean | null;
};

export type UseGetCollectionsOptions = {
  paginated?: boolean;
  pageSize?: number;
  filters?: CollectionListFilters;
};

type PaginationInfo = {
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
};

type ReturnedProps = {
  isLoadingCollections: boolean;
  collections: Collection[];
  pagination: PaginationInfo;
  setPage: (page: number) => void;
  addCollection: (c: Collection) => Promise<void>;
  duplicateCollection: (id: number) => Promise<void>;
  removeCollection: (c: Collection) => Promise<void>;
  removeItem: (c: Item) => Promise<void>;
  addItem: (c: Item) => Promise<void>;
  updateItem: (c: Item) => Promise<void>;
  updateCollection: (c: Collection) => Promise<void>;
  reorderCollectionItems: (c: Collection) => Promise<void>;
  moveItemToCollection: (item: Item, targetCollection: Collection) => Promise<void>;
  expandedCollection: Collection | null;
  setExpandedCollection: any;
  refresh: () => Promise<void>;
};

const EMPTY_PAGINATION: PaginationInfo = {
  totalElements: 0,
  totalPages: 0,
  size: 10,
  number: 0,
};

export function useGetCollections(options: UseGetCollectionsOptions = {}): ReturnedProps {
  const { paginated = false, pageSize = 10, filters } = options;
  const visibility = filters?.visibility;
  const pricesArePublic = filters?.pricesArePublic;

  const { t } = useTranslation();
  const [collections, setCollections] = useState<Collection[]>([]);
  const [expandedCollection, setExpandedCollection] = useState<Collection | null>(null);
  const [isLoadingCollections, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [pagination, setPagination] = useState<PaginationInfo>({ ...EMPTY_PAGINATION, size: pageSize });
  const { uploadImage } = useImageUploader('', (resp: any) => resp.data, "catalogues");

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      if (paginated) {
        const response = await CollectionsAPI.searchCollections({
          page: currentPage,
          size: pageSize,
          visibility,
          pricesArePublic: pricesArePublic === null || pricesArePublic === undefined ? undefined : pricesArePublic,
        });
        setCollections(response.data.content);
        setPagination({
          totalElements: response.data.totalElements,
          totalPages: response.data.totalPages,
          size: response.data.size,
          number: response.data.number,
        });
      } else {
        const collectionsRaw = await CollectionsAPI.getCollections();
        setCollections(collectionsRaw.data);
        setPagination({ ...EMPTY_PAGINATION, size: pageSize, totalElements: collectionsRaw.data.length, totalPages: 1 });
      }
    } catch (err) {
      console.error('Failed to load catalogues', { paginated, currentPage, error: err });
      toastService.showToast(`${t('toast.errors.catalogues.load')}`, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [t, paginated, currentPage, pageSize, visibility, pricesArePublic]);

  const refresh = useCallback(async () => {
    await load();
  }, [load]);

  const setPage = (page: number) => {
    setCurrentPage(page);
  };

  const addImageToItem = async (collectionId: number, item: Item, file: File): Promise<Item | string> => {
    const itemWithImage = await uploadImage(file, `collections/${collectionId}/${item.id}/files/images`, { media: item.media });
    return itemWithImage;
  };

  const addCollection = async (collection: Collection) => {
    setIsLoading(true);
    await CollectionsAPI.add(collection);
    await load();
  };

  const duplicateCollection = async (id: number) => {
    setIsLoading(true);
    await CollectionsAPI.duplicate(id);
    await load();
  };

  const addItem = async (item: Item) => {
    setIsLoading(true);
    try {
      let newItem = (await CollectionsAPI.addItem(expandedCollection?.id!, item)).data;
      if (!!item.files) {
        for (let tempFile of item.files) {
          try {
            const urlOrUpdatedItem = await addImageToItem(expandedCollection?.id!, newItem, tempFile);
            if (typeof urlOrUpdatedItem === 'string') {
              newItem.media = newItem.media || [];
              newItem.media.push(simplifyUrl(urlOrUpdatedItem));
            } else {
              newItem.media = urlOrUpdatedItem.media;
            }
          } catch (error) {
            console.error('Failed to add catalogue item image', { collectionId: expandedCollection?.id, error });
            toastService.showToast(`${t("toast.errors.add_file")}: + ${tempFile.name}`, 'error');
          }
        }
      }
      await CollectionsAPI.updateItem(expandedCollection!.id!, newItem.id!, newItem);
      await load();
    } catch (error) {
      console.error('Failed to add catalogue item', { collectionId: expandedCollection?.id, error });
      toastService.showToast(`${t("toast.errors.catalogues.item.add")}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const updateCollection = async (collection: Collection) => {
    setIsLoading(true);
    await CollectionsAPI.update(collection.id!, collection);
    await load();
  };

  const reorderCollectionItems = async (collection: Collection) => {
    await CollectionsAPI.update(collection.id!, collection);
    await load();
  };

  const moveItemToCollection = async (item: Item, targetCollection: Collection) => {
    if (!item.id || !targetCollection.id) {
      return;
    }
    if (item.collection === targetCollection.id) {
      return;
    }
    setIsLoading(true);
    try {
      const payload = buildItemPayloadForMoveToCollection(item, targetCollection);
      await CollectionsAPI.updateItem(targetCollection.id, item.id, payload);
      toastService.showToast(t('toast.success.catalogues.item.move'));
      await load();
    } catch (error) {
      console.error('Failed to move catalogue item', { itemId: item.id, targetCollectionId: targetCollection.id, error });
      toastService.showToast(t('toast.errors.catalogues.item.move'), 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const updateItem = async (item: Item) => {
    setIsLoading(true);
    let newItem = { ...item };
    newItem.media = newItem.media || [];
    try {
      if (!!item.files) {
        for (let tempFile of item.files) {
          try {
            const urlOrUpdatedItem = await addImageToItem(item.collection!, newItem, tempFile);
            if (typeof urlOrUpdatedItem === 'string') {
              newItem.media.push(simplifyUrl(urlOrUpdatedItem));
            } else {
              const newMedia: Set<string> = new Set(newItem.media);
              urlOrUpdatedItem.media?.forEach(mediaUrl => newMedia.add(mediaUrl));
              newItem.media = Array.from(newMedia);
            }
          } catch (error) {
            console.error('Failed to update catalogue item image', { itemId: item.id, error });
            toastService.showToast(`${t("toast.errors.add_file")}: + ${tempFile.name}`, 'error');
          }
        }
      }
      await CollectionsAPI.updateItem(item.collection!, item.id!, newItem);
      await load();
    } catch (error) {
      console.error('Failed to update catalogue item', { itemId: item.id, error });
      toastService.showToast(`${t("toast.errors.catalogues.item.update")}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const removeCollection = async (collection: Collection) => {
    setIsLoading(true);
    await CollectionsAPI.remove(collection.id!);
    await load();
  };

  const removeItem = async (item: Item) => {
    try {
      setIsLoading(true);
      await CollectionsAPI.removeItem(item.collection!, item.id!);
      await load();
    } catch (error) {
      console.error('Failed to remove catalogue item', { itemId: item.id, error });
      toastService.showToast(`${t("toast.errors.catalogues.item.remove")}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, [load]);

  return {
    isLoadingCollections,
    collections,
    pagination,
    setPage,
    addCollection,
    duplicateCollection,
    removeCollection,
    updateCollection,
    expandedCollection,
    setExpandedCollection,
    removeItem,
    updateItem,
    addItem,
    reorderCollectionItems,
    moveItemToCollection,
    refresh
  };
}
